"use client";

import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { StarRating } from "@/components/landing/star-rating";
import { TESTIMONIALS_ANCHOR_ID } from "@/lib/testimonials-anchor";
import { cn } from "@/lib/utils";
import type { Testimonial } from "@/lib/testimonials";

/**
 * Zeigt freigegebene Bewertungen zusammengeklappt als reine Sterne-Zusammenfassung
 * (Erik 2026-07-28: "Sterne in einem guten UI sehen, sobald die ersten sehr guten
 * Bewertungen abgegeben wurden"), auf Klick ausgeklappt mit den einzelnen Kommentaren
 * ("dass man es dann ausklappen kann und direkt die Kommentare sieht"). Rendert nichts,
 * solange keine Bewertung freigegeben ist - das Bewertungsformular (ReviewForm) bleibt
 * davon unabhaengig immer sichtbar.
 */
export function TestimonialsDisplay({
  testimonials,
  averageRating,
  count,
}: {
  testimonials: Testimonial[];
  averageRating: number | null;
  count: number;
}) {
  const [expanded, setExpanded] = useState(false);

  // Landingpage-Feedback 2026-08-17, Punkt 11: der Klick auf HeroRatingBadge landete zwar
  // korrekt auf dieser Section (Anker "bewertungen" war technisch schon richtig verdrahtet),
  // zeigte aber nur die eingeklappte Sterne-Zusammenfassung statt direkt der Kommentare -
  // wirkte fuer Erik wie ein "falscher" Link. Fix: beim Ankommen ueber genau diesen Anker
  // (Erst-Laden UND spaeteres erneutes Klicken waehrend die Seite schon offen ist) sofort
  // ausklappen, statt einen zweiten Klick zu verlangen.
  useEffect(() => {
    const expandIfAnchored = () => {
      if (window.location.hash === `#${TESTIMONIALS_ANCHOR_ID}`) setExpanded(true);
    };
    expandIfAnchored();
    window.addEventListener("hashchange", expandIfAnchored);
    return () => window.removeEventListener("hashchange", expandIfAnchored);
  }, []);

  if (averageRating === null) return null;

  return (
    <div className="flex w-full flex-col items-center">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-ink"
      >
        <StarRating rating={Math.round(averageRating)} />
        <span>
          {averageRating.toFixed(1)} · {count} {count === 1 ? "Bewertung" : "Bewertungen"}
        </span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} aria-hidden="true" />
      </button>
      {expanded && (
        <div className="mt-6 grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          {testimonials.map((t) => (
            <Card key={t.id} className="border-gold/40">
              <CardContent className="flex flex-col gap-3 pt-6">
                <StarRating rating={t.rating} />
                <p className="font-serif text-lg leading-snug text-ink">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-sm font-medium text-muted-foreground">
                  {t.author}{" "}
                  <span className="font-normal text-xs">({new Date(t.createdAt).toLocaleDateString("de-DE")})</span>
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
